var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState } from "react";
import { StepType } from "@types";
import { findSourceById, getSelectedStepDetailsById, handleStepFieldValueChanges, registerStep, validateFieldConfiguration, validateSource, validateStep, validateStepNameAndId } from "@utils";
import { FormSection } from "@components/FormSection/FormSection";
import { SourceSelectorInput } from "../SourceSelector/SourceSelectorInput";
import { SourceMapper, validateConditions } from "../SourceMapper/SourceMapper";
import { FieldMapper } from "../FieldMapper/FieldMapper";
import { Checkbox, hasValue } from "uxp/components";
import Icon from '@images/join.svg';
export function renderStep(context, query, onChange, selectedStepId) {
    const { step, index, previousSteps, selectedSources } = getSelectedStepDetailsById(query, selectedStepId);
    const _step = Object.assign({}, step);
    const $L = (key) => context.uxpContext.$L(key);
    const [errors, setErrors] = useState({});
    useEffect(() => {
        const { isValid, errors } = validateStep(query, step);
        const resolved = Object.fromEntries(Object.entries(errors || {}).map(([k, v]) => [k, v && v.startsWith('lqe.') ? $L(v) : v]));
        setErrors(resolved);
    }, [step]);
    const source1 = findSourceById(query, _step === null || _step === void 0 ? void 0 : _step.source1);
    const source2 = findSourceById(query, _step === null || _step === void 0 ? void 0 : _step.source2);
    function onValueChange(field, value, source) {
        return __awaiter(this, void 0, void 0, function* () {
            const _query = yield handleStepFieldValueChanges(context, query, selectedStepId, field, value, source);
            onChange(_query);
        });
    }
    return React.createElement(React.Fragment, null,
        React.createElement(FormSection, { label: $L('lqe.step.join.source-01'), error: errors === null || errors === void 0 ? void 0 : errors.source1 },
            React.createElement(SourceSelectorInput, { source: source1, onChange: newSource => {
                    onValueChange('source1', newSource, true);
                }, availableSources: [...previousSteps, ...selectedSources], query: query })),
        React.createElement(FormSection, { label: $L('lqe.step.join.source-02'), error: errors === null || errors === void 0 ? void 0 : errors.source2 },
            React.createElement(SourceSelectorInput, { source: source2, onChange: newSource => {
                    onValueChange('source2', newSource, true);
                }, availableSources: [...previousSteps, ...selectedSources], query: query })),
        React.createElement(FormSection, { label: $L('lqe.step.join.match-by'), error: errors === null || errors === void 0 ? void 0 : errors.on },
            React.createElement(SourceMapper, { conditions: (_step === null || _step === void 0 ? void 0 : _step.on) || [], source1: source1, source2: source2, onChange: conditions => onValueChange('on', conditions) })),
        React.createElement(FormSection, { label: $L('lqe.step.join.fields'), error: errors === null || errors === void 0 ? void 0 : errors.source2Fields },
            React.createElement(FieldMapper, { fields: (_step === null || _step === void 0 ? void 0 : _step.source2Fields) || [], source: source2, onChange: fields => onValueChange('source2Fields', fields) })),
        React.createElement(FormSection, { label: $L('lqe.step.join.optional'), error: '' },
            React.createElement(Checkbox, { checked: _step.optional, onChange: s => onValueChange('optional', s) })));
}
function validate(query, step) {
    var _a, _b, _c;
    const errors = validateStepNameAndId(step);
    // validate source
    const source1 = validateSource(query, step, 'source1');
    if (!source1.isValid)
        errors.source1 = source1.error;
    const source2 = validateSource(query, step, 'source2');
    if (!source2.isValid)
        errors.source2 = source2.error;
    const conditions = validateConditions(step === null || step === void 0 ? void 0 : step.on);
    if (!conditions.isValid)
        errors.on = conditions.error;
    else {
        const { uniqueAttributesSource1, uniqueAttributesSource2 } = conditions;
        const fields1 = validateFieldConfiguration(query, step, uniqueAttributesSource1, 'source1');
        if (!fields1.isValid)
            errors.on = fields1.error;
        else {
            const fields2 = validateFieldConfiguration(query, step, uniqueAttributesSource2, 'source2');
            if (!fields2.isValid)
                errors.on = fields2.error;
        }
    }
    if (!(step === null || step === void 0 ? void 0 : step.source2Fields) || ((_a = step === null || step === void 0 ? void 0 : step.source2Fields) === null || _a === void 0 ? void 0 : _a.length) == 0)
        errors.source2Fields = 'lqe.step.join.error-no-fields';
    else {
        const fields3 = validateFieldConfiguration(query, step, (_b = step === null || step === void 0 ? void 0 : step.source2Fields) === null || _b === void 0 ? void 0 : _b.map(f => f.field), 'source2');
        if (!fields3.isValid)
            errors.source2Fields = fields3.error;
        // check if selected field/alias already exists in source 1
        const source1Obj = findSourceById(query, step.source1);
        if (source1Obj) {
            // Collect all field names and aliases from source1
            const source1Fields = new Set((_c = source1Obj === null || source1Obj === void 0 ? void 0 : source1Obj.schema) === null || _c === void 0 ? void 0 : _c.map(field => field === null || field === void 0 ? void 0 : field.name));
            // Check for conflicts in source2Fields
            for (const fieldObj of step.source2Fields) {
                const fieldName = hasValue(fieldObj.newField) ? fieldObj.newField : fieldObj.field;
                if (source1Fields.has(fieldName)) {
                    errors.source2Fields = `Field or alias "${fieldName}" already exists in Source 1. Please choose a unique name.`;
                    break;
                }
            }
        }
    }
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}
registerStep({
    type: StepType.JOIN,
    name: "lqe.step.join.name",
    description: "lqe.step.join.description",
    icon: Icon,
    iconStyles: {
        backgroundSize: '110%'
    },
    render: renderStep,
    validate: validate,
    onSelectStep: (basicProps, previousSource) => {
        return Object.assign(Object.assign({}, basicProps), { source1: previousSource, source2: '', on: [], optional: false, source2Fields: [] });
    }
});
